import PedidoVendaLink, { semCamposInternos } from "components/PedidoVendaLink";
import { Alert, Box, Button, Card, Grid, Icon, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { format } from "utils/formatDate";

import { DataGridPro as DataGrid } from "@mui/x-data-grid-pro";
import MDButton from "components/MDButton";
import { useFormik } from "formik";
import AdicionarFornecedor from "layouts/fornecedores";
import { TIPO_FRETE } from "layouts/pedidos-compra/components/yupSchema";
import {
  STATUS_PEDIDO,
  findStatusPedido,
} from "layouts/pedidos-venda/components/yupSchema";
import { useEffect, useState } from "react";
import Service from "services/payables";
import PurchasesService from "services/purchases";
import { ptBR_DataGrid } from "utils/datagrid";
import * as XLSX from "xlsx";

const initialValues = {
  startDate: "",
  endDate: "",
  supplier: null,
  status: "",
};

export default function AcertoFornecedor() {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState([]);
  const [originalReport, setOriginalReport] = useState([]);

  const [showAddFornecedor, setShowAddFornecedor] = useState(false);
  const [childData, setChildData] = useState({});

  const [totalListado, setTotalListado] = useState(0);
  const [totalReceber, setTotalReceber] = useState(0);
  const [totalFaltaPagar, setTotalFaltaPagar] = useState(0);

  const [rowsBase, setRowsBase] = useState([]);
  const [erroAjuste, setErroAjuste] = useState("");

  const widths = [110, 60, 140, 60, 60, 60, 60, 60, 60, 140, 140, 110, 130];

  // Colunas editáveis -> campo do ajuste gravado no pedido de compra
  const COLUNAS_EDITAVEIS = {
    "Vlr. Unit.": "price",
    "Peso Líquido": "peso",
    "Descarte (kg)": "descarte",
  };

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: async (values) => {
      setLoading(true);

      try {
        const rows = await Service.agreementWithSuppliers({
          ...values,
          supplier: values.supplier?._id || values.supplier,
        });

        const rowsSemLetter = rows.filter(
          (row) =>
            row.sale.letterPedido === "" ||
            row.sale.letterPedido === undefined ||
            row.sale.letterPedido === null ||
            row.sale.letterPedido === "A"
        );

        setTotalListado(
          rowsSemLetter.reduce(
            (acc, row) => acc + Number(row.purchase?.valorTotal),
            0
          )
        );

        setTotalReceber(
          rowsSemLetter.reduce(
            (acc, row) =>
              acc +
              (Number(row.sale?.valorTotal) -
                Number(row.sale?.totalPagarFrete || 0)),
            0
          )
        );

        // Falta pagar: soma por pedido de compra (sem repetir quando há várias vendas)
        const pedidosSomados = new Set();
        setTotalFaltaPagar(
          rowsSemLetter.reduce((acc, row) => {
            const id = row.purchase?._id;
            if (!id || pedidosSomados.has(id)) return acc;
            pedidosSomados.add(id);
            return acc + Number(row.contasPagar?.aberto || 0);
          }, 0)
        );

        setOriginalReport(rows);
        setRowsBase(rowsSemLetter);
        const agreements = await parseAgreements(rowsSemLetter);

        if (!agreements.length) {
          alert("Nenhum resultado encontrado");
        }

        setReport(agreements);

        setLoading(false);
      } catch (error) {
        console.log(error);
      }

      setLoading(false);
    },
  });

  const parseAgreements = async (rowsOriginais) => {
    if (!rowsOriginais || rowsOriginais.length === 0) return [];
    // Trabalha numa cópia: o cálculo abaixo altera valorTotal e o relatório
    // é recalculado a cada ajuste.
    const rows = JSON.parse(JSON.stringify(rowsOriginais));
    const data = [];

    const mapaPedidoJaExibiu = new Map();

    for (const row of rows) {
      if (row.sale?.valorTotal === 0 || !row.sale?.valorTotal) {
        row.sale.valorTotal = row.sale?.products?.reduce((acc, product) => {
          return acc + Number(product.price) * Number(product.quantity);
        }, 0);
      }

      if (row.sale?.valorTotal > 0) {
        if (row.sale?.valorDescarga && row.sale?.valorDescarga > 0) {
          row.sale.valorTotal -= row.sale.valorDescarga;
        }

        if (row.sale?.totalPagarFrete && row.sale?.totalPagarFrete > 0) {
          if (
            row.sale?.tipoFrete === TIPO_FRETE.PRIME ||
            row.sale?.tipoFrete === TIPO_FRETE.FOB
          ) {
            row.sale.valorTotal -= row.sale.totalPagarFrete;
          }
        }
      }

      if (row.purchase?.valorTotal === 0 || !row.purchase?.valorTotal) {
        row.purchase.valorTotal = row.purchase?.products?.reduce(
          (acc, product) => {
            return acc + Number(product.price) * Number(product.quantity);
          },
          0
        );
      }

      const products = row.purchase?.products || [];
      const saleProducts = row.sale?.products || [];

      const totalQtdProdutos = products.reduce(
        (acc, product) => acc + product.quantity,
        0
      );

      for (let i = 0; i < Math.max(products.length, saleProducts.length); i++) {
        const purchaseProduct = products[i] || {};
        const saleProduct = saleProducts[i] || {};

        const jaExibiu = mapaPedidoJaExibiu.get(row.purchase?.numPedido);

        const productId =
          purchaseProduct?.product?._id || purchaseProduct?.product || null;
        const ajuste =
          (productId &&
            (row.purchase?.acertoFornecedor || []).find(
              (a) =>
                String(a.product) === String(productId) &&
                (a.indice === undefined || a.indice === null
                  ? true
                  : a.indice === i)
            )) ||
          {};
        const temAjuste = (campo) =>
          ajuste[campo] !== undefined && ajuste[campo] !== null;

        const pesoCalculado =
          products.length > 1
            ? purchaseProduct?.quantity
            : row.purchase?.pesoLiquido;
        const pesoLiquido = temAjuste("peso")
          ? Number(ajuste.peso)
          : Number(pesoCalculado || 0);

        const pesoComDescarteCalculado =
          products.length > 1
            ? calculaPesoProdutoComDescarte(pesoLiquido, totalQtdProdutos)
            : calculaPesoComDescarte(pesoLiquido);
        const descarte = temAjuste("descarte")
          ? Number(ajuste.descarte)
          : pesoLiquido - pesoComDescarteCalculado;
        const pesoLiquidoComDescarte = pesoLiquido - descarte;

        const preco = temAjuste("price")
          ? Number(ajuste.price)
          : Number(purchaseProduct?.price || 0);

        data.push({
          Emissão: format(new Date(row.purchase?.data), "dd/MM/yyyy"),
          _saleId: row.sale?._id,
          _purchaseId: row.purchase?._id,
          _productId: productId,
          _indice: i,
          _ajustado: {
            price: temAjuste("price"),
            peso: temAjuste("peso"),
            descarte: temAjuste("descarte"),
          },
        Pedido: row.purchase?.numPedido + row.sale?.letterPedido,
          "Descrição do Produto":
            purchaseProduct?.product?.descricao ||
            saleProduct?.product?.descricao ||
            "",
          Média: jaExibiu ? "-" : Number(row.purchase.media).toFixed(2),
          "Vlr. Unit.": Math.round(preco * 100) / 100,
          "Peso Líquido": Math.round(pesoLiquido * 100) / 100,
          "Descarte (kg)": Math.round(descarte * 100) / 100,
          "Peso Líquido à Pagar": Math.round(pesoLiquidoComDescarte * 100) / 100,
          "Vlr. Total": Number(preco * pesoLiquido).toFixed(2),
          "Vlr. Com Descarte à Pagar": Number(
            preco * pesoLiquidoComDescarte
          ).toFixed(2),
          Turma: row.purchase?.turma,
          Carregamento: row.purchase?.carregamento,
          Transportadora: jaExibiu
            ? "-"
            : row.purchase?.shippingCompany?.nomeFantasia ||
              row.purchase?.shippingCompany?.razaoSocial ||
              row.purchase?.motorista,
          Veículo: jaExibiu ? "-" : row.purchase?.caminhao,
          Status: jaExibiu ? "-" : findStatusPedido(row.sale?.status) || "",
          "Total à Receber": jaExibiu
            ? "-"
            : row.sale?.valorTotal
            ? Number(Number(row.sale?.valorTotal)).toFixed(2)
            : "",
          "Contas à Pagar": jaExibiu
            ? "-"
            : Number(row.contasPagar?.total || 0).toFixed(2),
          "Pago": jaExibiu ? "-" : Number(row.contasPagar?.pago || 0).toFixed(2),
          "Falta Pagar": jaExibiu
            ? "-"
            : Number(row.contasPagar?.aberto || 0).toFixed(2),
        });

        if (!jaExibiu) {
          mapaPedidoJaExibiu.set(row.purchase?.numPedido, true);
        }
      }
    }

    return data;
  };

  const calculaPesoComDescarte = (pesoReal) => {
    let descarte = pesoReal * 0.02;

    if (pesoReal < 18000) {
      descarte += 1000;
    } else if (pesoReal >= 18000 && pesoReal < 26000) {
      descarte += 1500;
    } else {
      descarte += 2000;
    }

    return pesoReal - descarte;
  };

  const calculaPesoProdutoComDescarte = (pesoReal, totalQtdProdutos) => {
    let descarte = pesoReal * 0.02;

    if (totalQtdProdutos < 18000) {
      descarte += (1000 / totalQtdProdutos) * pesoReal;
    } else if (totalQtdProdutos >= 18000 && totalQtdProdutos < 26000) {
      descarte += (1500 / totalQtdProdutos) * pesoReal;
    } else {
      descarte += (2000 / totalQtdProdutos) * pesoReal;
    }

    return pesoReal - descarte;
  };

  const descobreTurma = (pesoLiquido) => {
    if (pesoLiquido < 17000) {
      return 400;
    } else if (pesoLiquido >= 17000 && pesoLiquido < 22000) {
      return 600;
    } else {
      return 900;
    }
  };

  const printAgreements = (agreements) => {
    // Criar um novo workbook
    const workbook = XLSX.utils.book_new();

    // Converter os dados para worksheet
    const worksheet = XLSX.utils.json_to_sheet(agreements);

    // Definir tipos de dados para colunas específicas
    const range = XLSX.utils.decode_range(worksheet["!ref"]);

    for (let col = range.s.c; col <= range.e.c; col++) {
      const colLetter = XLSX.utils.encode_col(col);
      const headerCell = worksheet[colLetter + "1"];

      if (headerCell) {
        const headerValue = headerCell.v;

        // Definir formato numérico para colunas de valores
        if (
          headerValue.includes("Vlr.") ||
          headerValue.includes("Média") ||
          headerValue.includes("Turma") ||
          headerValue.includes("Total à Receber") ||
          headerValue === "Contas à Pagar" ||
          headerValue === "Pago" ||
          headerValue === "Falta Pagar"
        ) {
          for (let row = range.s.r + 1; row <= range.e.r; row++) {
            const cellAddress = colLetter + (row + 1);
            const cell = worksheet[cellAddress];

            if (cell && cell.v !== "-" && cell.v !== "") {
              // Converter para número se for string
              if (typeof cell.v === "string") {
                cell.v = parseFloat(cell.v.replace(",", "."));
              }
              // Definir formato numérico
              cell.t = "n";
              cell.z = "#,##0.00";
            }
          }
        }
      }
    }

    // Adicionar a worksheet ao workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Acerto Fornecedor");

    // Gerar e baixar o arquivo Excel
    XLSX.writeFile(workbook, "acerto_" + new Date().getTime() + ".xlsx");
  };

  const selectParent = (parent, value) => {
    formik.setFieldValue(parent, value);
  };

  useEffect(() => {
    const closeOnEscapePressed = (e) => {
      if (e.key === "Escape") {
        if (showAddFornecedor) setShowAddFornecedor(false);
      }
    };
    window.addEventListener("keydown", closeOnEscapePressed);
    return () => window.removeEventListener("keydown", closeOnEscapePressed);
  }, [showAddFornecedor]);

  // Grava o ajuste editado na tabela e recalcula o relatório.
  const salvarAjuste = async (novaLinha, linhaAntiga) => {
    const coluna = Object.keys(COLUNAS_EDITAVEIS).find(
      (c) => novaLinha[c] !== linhaAntiga[c]
    );
    if (!coluna) return linhaAntiga;

    const bruto = novaLinha[coluna];
    const valor =
      bruto === null || bruto === "" || Number.isNaN(Number(bruto))
        ? null
        : Number(bruto);

    if (valor !== null && valor < 0) {
      setErroAjuste("O valor não pode ser negativo.");
      return linhaAntiga;
    }

    const { acertoFornecedor } = await PurchasesService.acertoFornecedor(
      novaLinha._purchaseId,
      {
        product: novaLinha._productId,
        indice: novaLinha._indice,
        campo: COLUNAS_EDITAVEIS[coluna],
        valor,
      }
    );

    const novasRows = rowsBase.map((r) =>
      r.purchase?._id === novaLinha._purchaseId
        ? { ...r, purchase: { ...r.purchase, acertoFornecedor } }
        : r
    );
    setRowsBase(novasRows);
    const agreements = await parseAgreements(novasRows);
    setReport(agreements);
    setErroAjuste("");

    return agreements[novaLinha.id]
      ? { ...agreements[novaLinha.id], id: novaLinha.id }
      : novaLinha;
  };

  return (
    <DashboardLayout>
      <MDBox pt={3} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <MDTypography variant="h6" color="white" mt={1}>
                  Relatório de Acerto com Fornecedor
                </MDTypography>
              </MDBox>
              <MDBox pt={3} mx={2} py={3} px={2}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: 2,
                    mb: 2,
                  }}
                  component="form"
                  onSubmit={formik.handleSubmit}
                >
                  <TextField
                    id="startDate"
                    name="startDate"
                    label="Data Inicial"
                    type="date"
                    value={formik.values.startDate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={loading}
                    error={
                      formik.touched.startDate &&
                      Boolean(formik.errors.startDate)
                    }
                    helperText={
                      formik.touched.startDate && formik.errors.startDate
                    }
                    sx={{
                      width: "100%",
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />

                  <TextField
                    id="endDate"
                    name="endDate"
                    label="Data Final"
                    type="date"
                    value={formik.values.endDate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={loading}
                    error={
                      formik.touched.endDate && Boolean(formik.errors.endDate)
                    }
                    helperText={formik.touched.endDate && formik.errors.endDate}
                    sx={{
                      width: "100%",
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />

                  <Alert
                    icon={false}
                    severity="success"
                    action={
                      <MDButton
                        variant="gradient"
                        color="info"
                        fullWidth
                        iconOnly
                        onClick={() => setShowAddFornecedor(true)}
                      >
                        <Icon fontSize="small">edit</Icon>
                      </MDButton>
                    }
                  >
                    <b>Fornecedor: </b>
                    {childData.supplier?.razaoSocial ||
                      childData.supplier?.nomeFantasia}
                  </Alert>

                  <TextField
                    id="status"
                    name="status"
                    label="Status"
                    value={formik.values.status}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={loading}
                    error={
                      formik.touched.status && Boolean(formik.errors.status)
                    }
                    helperText={formik.touched.status && formik.errors.status}
                    sx={{
                      width: "100%",
                    }}
                    select
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option aria-label="Selecione" value="" />

                    {Object.values(STATUS_PEDIDO).map((opcao) => (
                      <option key={opcao} value={opcao}>
                        {findStatusPedido(opcao)}
                      </option>
                    ))}
                  </TextField>

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    sx={{
                      gridColumn: "span 2",
                      justifySelf: "center",
                      width: "100%",
                      color: "white !important",
                    }}
                  >
                    {loading ? "Carregando..." : "Gerar Relatório"}
                  </Button>
                </Box>
              </MDBox>
            </Card>

            {report.length > 0 && (
              <>
                <Alert severity="info" sx={{ mt: 4 }}>
                  Dê dois cliques em <b>Vlr. Unit.</b>, <b>Peso Líquido</b> ou{" "}
                  <b>Descarte (kg)</b> para ajustar o acerto. Os ajustes ficam
                  salvos no pedido de compra (destacados em amarelo) e não
                  alteram os produtos, totais ou contas do pedido. Apague o
                  valor para voltar ao calculado.
                </Alert>
                {erroAjuste && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {erroAjuste}
                  </Alert>
                )}
                <Card sx={{ marginTop: 2 }}>
                  <DataGrid
                    initialState={{
                      pagination: { paginationModel: { pageSize: 100 } },
                    }}
                    pageSizeOptions={[100, 200]}
                    rows={report.map((row, index) =>
                      Object({ ...row, id: index })
                    )}
                    columns={Object.keys(report[0])
                      .filter((key) => !key.startsWith("_"))
                      .map((key, idx) =>
                        Object({
                          headerName: key,
                          field: key,
                          flex: 1,
                          idx: idx,
                          minWidth: widths[idx],
                          ...(COLUNAS_EDITAVEIS[key] && {
                            editable: true,
                            type: "number",
                            cellClassName: (params) =>
                              params.row._ajustado?.[COLUNAS_EDITAVEIS[key]]
                                ? "acerto-ajustado"
                                : "",
                          }),
                          ...(key === "Pedido" && {
                            renderCell: (params) => (
                              <PedidoVendaLink saleId={params.row._saleId}>
                                {params.value}
                              </PedidoVendaLink>
                            ),
                          }),
                        })
                      )}
                    rowCount={report.length}
                    rowSelection={false}
                    isCellEditable={(params) =>
                      Boolean(params.row._purchaseId && params.row._productId)
                    }
                    processRowUpdate={salvarAjuste}
                    onProcessRowUpdateError={(error) =>
                      setErroAjuste(
                        error?.error ||
                          error?.response?.data?.error ||
                          error?.message ||
                          "Não foi possível salvar o ajuste."
                      )
                    }
                    localeText={ptBR_DataGrid}
                    sx={{
                      ".MuiDataGrid-virtualScroller": {
                        minHeight: "100px",
                      },
                      "& .acerto-ajustado": {
                        backgroundColor: "#fff8e1",
                        fontWeight: 600,
                      },
                    }}
                    disableColumnFilter
                    headerFilters
                    slots={{
                      headerFilterMenu: null,
                    }}
                  />
                </Card>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    p: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={() => printAgreements(semCamposInternos(report))}
                    sx={{
                      color: "white !important",
                    }}
                  >
                    Exportar para Excel
                  </Button>
                </Box>
              </>
            )}

            {(totalListado > 0 || totalReceber > 0 || totalFaltaPagar > 0) && (
              <Card sx={{ marginTop: 4 }}>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                  sx={{ display: "flex", justifyContent: "space-between" }}
                >
                  <MDTypography variant="h6" color="white" mt={1}>
                    Totais
                  </MDTypography>
                </MDBox>
                <MDBox pt={3} mx={2} py={3} px={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <MDTypography variant="h6" color="info">
                        Total Listado
                      </MDTypography>
                    </Grid>
                    <Grid item xs={6}>
                      <MDTypography variant="h6" color="info">
                        {new Intl.NumberFormat("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        }).format(totalListado)}
                      </MDTypography>
                    </Grid>

                    <Grid item xs={6}>
                      <MDTypography variant="h6" color="info">
                        Total à Receber
                      </MDTypography>
                    </Grid>
                    <Grid item xs={6}>
                      <MDTypography variant="h6" color="info">
                        {new Intl.NumberFormat("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        }).format(totalReceber)}
                      </MDTypography>
                    </Grid>

                    <Grid item xs={6}>
                      <MDTypography variant="h6" color="info">
                        Total Falta Pagar (contas a pagar dos pedidos)
                      </MDTypography>
                    </Grid>
                    <Grid item xs={6}>
                      <MDTypography variant="h6" color="info">
                        {new Intl.NumberFormat("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        }).format(totalFaltaPagar)}
                      </MDTypography>
                    </Grid>
                  </Grid>
                </MDBox>
              </Card>
            )}
          </Grid>
        </Grid>
      </MDBox>

      {showAddFornecedor && (
        <MDBox
          pt={"30px"}
          pb={"5%"}
          px={"5%"}
          sx={{
            position: "absolute",
            zIndex: 9999,
            width: "100vw",
            minHeight: "100vh",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(5px)",
            top: 0,
            right: 0,
            overflow: "auto",
          }}
        >
          <AdicionarFornecedor
            fromParent={true}
            selectParent={(value) => {
              setChildData((prevState) =>
                Object({ ...prevState, supplier: value })
              );
              selectParent("supplier", value?._id);
            }}
            setShowAddFornecedor={setShowAddFornecedor}
          />
        </MDBox>
      )}
      <Footer />
    </DashboardLayout>
  );
}
